// @ts-nocheck
import React, { useState, useEffect, useCallback } from 'react';
import { BsArrowRight, BsArrowLeft } from 'react-icons/bs';
import ReactLoader from 'react-loading';
import { useSelector } from 'react-redux';

import { ENV } from '@environment';
import * as Sentry from '@sentry/react';
import moment from 'moment';
import { RootState } from 'store';
import { ColorPalette } from 'theme/colors';

import { Box } from 'components/Buttons/Button/styles';
import Input from 'components/Input';
import Loader from 'components/Loader';
import useIsMobile from 'hooks/useIsMobile';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { addCreditCard } from 'services/paymentMethodService';
import { getTokenexAuthenticationKey } from 'services/paymentMethodService';

import CreditCardImage from './components/CreditCardImage';
import {
  Container,
  Content,
  FieldContainer,
  Label,
  Error,
  ButtonContainer,
  ButtonNavContainer,
  Button,
  StepContainer,
  LoaderContainer,
} from './styles';

const ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT as string;
const isProduction = !ENVIRONMENT.startsWith('DEV') && ENVIRONMENT !== 'LOCAL';

const LoaderButton = ({ fontColor }: { fontColor: string }) => (
  <Box>
    <ReactLoader type="spinningBubbles" color={fontColor} width={30} height={30} />
  </Box>
);

interface Props {
  groupId?: string;
  userId?: string;
  onAdded: (cardId?: string) => void;
  selectedMethod?: PaymentMethod;
  flag?: string;
}

type InputChangeField = {
  value: string;
  error: string;
  isFocused: boolean;
  isValid: boolean;
};

type InputChangeData = {
  card_number: InputChangeField;
  card_cvv: InputChangeField;
  expiration_date: InputChangeField;
  first_name: InputChangeField;
  last_name: InputChangeField;
};

type CardTypeData = {
  cardType: string;
};

enum ErrorTypes {
  IS_REQUIRED = 'IS_REQUIRED',
  INVALID_EXPIRATION_DATE = 'INVALID_EXPIRATION_DATE',
  INVALID_CARD_NUMBER = 'INVALID_CARD_NUMBER',
  INVALID_CVV = 'INVALID_CVV',
  INVALID_VALUE = 'INVALID_VALUE',
  EMPTY = '',
  INVALID_EMAIL = 'INVALID_EMAIL',
}

const adjustInputElements = (id: string) => {
  const all = document.querySelectorAll(`[id=${id}]`);
  const first = all[0];

  // Removes duplicates with the same ID
  if (all.length > 1) {
    Array.from(all)
      .slice(1)
      .forEach((element) => {
        element.remove();
      });
  }

  // Removes browser autocomplete
  if (first) {
    (first as HTMLInputElement).autocomplete = 'off';
  }
};

const CreditCard = ({ groupId, userId, onAdded, selectedMethod, flag }: Props) => {
  const toast = useToast();
  const isMobile = useIsMobile();
  const { t } = useStoreTranslation();
  const accountResult = useSelector((state: RootState) => state.user.accountResult);

  const [step, setStep] = useState(0);
  const [disabled, setDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [cardType, setCardType] = useState('');

  const [cardNumberError, setCardNumberError] = useState('');
  const [cvvError, setCvvError] = useState('');
  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [expirationError, setExpirationError] = useState('');

  const [cardNumberText, setCardNumberText] = useState('');
  const [cvvText, setCvvText] = useState('');
  const [realCVV, setRealCVV] = useState('');
  const [firstNameText, setFirstNameText] = useState('');
  const [lastNameText, setLastNameText] = useState('');
  const [expiryText, setExpiryText] = useState('');
  const [emailText, setEmailText] = useState('');

  const [cardNumberValid, setCardNumberValid] = useState(false);
  const [cvvValid, setCvvValid] = useState(false);
  const [firstNameValid, setFirstNameValid] = useState(false);
  const [lastNameValid, setLastNameValid] = useState(false);
  const [expiryValid, setExpiryValid] = useState(false);
  const [emailValid, setEmailValid] = useState(false);

  const [nextDisabled, setNextDisabled] = useState(false);

  const handleNext = () => {
    setStep(step + 1);
    setNextDisabled(true);
  };

  const handlePrev = () => {
    setStep(step - 1);
  };

  const clearFields = () => {
    setCardNumberText('');
    setCvvText('');
    setFirstNameText('');
    setLastNameText('');
    setExpiryText('');
    setEmailText('');
    setCardNumberValid(false);
    setCvvValid(false);
    setFirstNameValid(false);
    setLastNameValid(false);
    setExpiryValid(false);
    setEmailValid(false);
  };

  useEffect(() => {
    setStep(1);
    setIsSubmitting(false);

    clearFields();
  }, []);

  const handleAddPaymentCard = async () => {
    setIsSubmitting(true);
    setDisabled(true);

    if (token !== '' && accountResult !== null) {
      try {
        const { data } = await addCreditCard(
          token,
          firstNameText,
          lastNameText,
          emailText,
          expiryText,
          userId ?? accountResult.id,
          accountResult.companyId,
          realCVV,
          groupId,
          last4digits,
          first6digits,
          '',
        );
        setIsSubmitting(false);
        setDisabled(false);
        onAdded(data.id);
      } catch (error) {
        Sentry.addBreadcrumb({
          category: 'payment',
          message: 'Could not add card',
          data: { error, message: error.message },
        });
        Sentry.captureMessage('[CreditCard] Could not add card');

        toast.add({
          type: 'danger',
          message: t('payment.cardForm.errors.generic'),
        });

        setIsSubmitting(false);
      }
    }
  };

  const handleChangeFirstName = () => {
    setFirstNameError('');
    if (firstNameText.length > 3) {
      setFirstNameValid(true);
    } else {
      setFirstNameError(messageError(ErrorTypes.IS_REQUIRED));
      setFirstNameValid(false);
    }
  };

  const handleChangeLastName = () => {
    setLastNameError('');
    if (lastNameText.length > 3) {
      setLastNameValid(true);
    } else {
      setLastNameError(messageError(ErrorTypes.IS_REQUIRED));
      setLastNameValid(false);
    }
  };

  const handleChangeEmail = () => {
    setEmailError('');
    if (emailText.match(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i)) {
      setEmailValid(true);
    } else {
      setEmailError(messageError(ErrorTypes.INVALID_EMAIL));
      setEmailValid(false);
    }
  };

  const handleChangeExpirationDate = () => {
    setExpirationError('');
    if (expiryText.length === 5) {
      const month = parseInt(expiryText.split('/')[0]);
      const year = parseInt(expiryText.split('/')[1]);
      const currentMonth = moment().month();
      const currentYear = parseInt(new Date().getFullYear().toString().substring(2, 4));

      let isValid = true;

      if (month <= 0 || month > 12) {
        setExpiryValid(false);
        isValid = false;
      }

      if (year < currentYear) {
        setExpiryValid(false);
        isValid = false;
      }

      if (year === currentYear && month <= currentMonth) {
        setExpiryValid(false);
        isValid = false;
      }

      if (isValid) {
        setExpiryValid(true);
      } else {
        setExpiryValid(false);
        setExpirationError(messageError(ErrorTypes.INVALID_EXPIRATION_DATE));
      }
    } else {
      setExpiryValid(false);
      setExpirationError(messageError(ErrorTypes.INVALID_EXPIRATION_DATE));
    }
  };

  const handleChangeCVV = () => {
    //CVV
    console.info('Validate cvv');
    setCvvValid(false);

    let cardCvvLength = 3;
    if (cardType === 'amex' || cardType === 'americanExpress') {
      cardCvvLength = 4;
    }

    if (realCVV.length === cardCvvLength) {
      console.info('Validate cvv - OK');
      setCvvValid(true);
      setCvvText(cardCvvLength === 4 ? '• • • •' : '• • •');
      setCvvError('');
    } else {
      setCvvError(messageError(ErrorTypes.INVALID_CVV));
      console.info('Validate cvv - NOK');
    }
  };

  const messageError = useCallback(
    (message: string) => {
      const ERRORS = [
        {
          id: ErrorTypes.IS_REQUIRED,
          message: t('payment.errors.IS_REQUIRED'),
        },
        {
          id: ErrorTypes.INVALID_EXPIRATION_DATE,
          message: t('payment.errors.INVALID_EXPIRATION_DATE'),
        },
        {
          id: ErrorTypes.INVALID_CARD_NUMBER,
          message: t('payment.errors.INVALID_CARD_NUMBER'),
        },
        {
          id: ErrorTypes.INVALID_CVV,
          message: t('payment.errors.INVALID_CVV'),
        },
        {
          id: ErrorTypes.INVALID_VALUE,
          message: t('payment.errors.INVALID_VALUE'),
        },
        {
          id: ErrorTypes.INVALID_EMAIL,
          message: t('error.email'),
        },
      ];

      const result = ERRORS.filter((e) => e.id === message);
      if (result.length > 0) {
        return result[0].message;
      }
      return message;
    },
    [t],
  );

  const [token, setToken] = React.useState<string>('');
  const [last4digits, setLast4Digits] = React.useState<string>('');
  const [first6digits, setFirst6Digits] = React.useState<string>('');

  const setTokenexEvents = (iframe) => {
    iframe.on('blur', function () {
      console.info('Tokenex - blur');
      iframe.tokenize();
    });

    iframe.on('change', function () {
      iframe.tokenize();
      iframe.validate();
      console.info('Tokenex - change');
    });

    iframe.on('validate', function (data) {
      console.info('Tokenex - validate', data);

      //CreditCardNumber
      console.info('Tokenex - validate cc');
      setCardNumberValid(false);
      if (data.isValid) {
        console.info('Tokenex - validate cc - OK');
        setCardNumberValid(true);
        setCardNumberText('•••• •••• •••• ••••');
        setCardNumberError('');

        setCardType(data.cardType);
      } else {
        setCardNumberError(messageError(ErrorTypes.INVALID_CARD_NUMBER));
        console.info('Tokenex - validate cc - NOK');
      }
    });

    iframe.on('tokenize', function (data) {
      console.log('Tokenex - tokenize', data);

      console.info('Tokenex - tokenize - validate token');
      if (data.token) {
        console.info('Tokenex - tokenize - validate token - OK', data.token);
        setToken(data.token);
        setLast4Digits(data.lastFour);
        setFirst6Digits(data.firstSix);
      } else {
        console.info('Tokenex - tokenize - validate token - NOK', data.token);
      }
    });

    iframe.on('load', function (data) {
      document.getElementById('tokenExIframeDiv').style.height = '42px';
      setIsLoading(false);
    });
  };

  const renderIFrame = async () => {
    const { data } = await getTokenexAuthenticationKey();

    const iframeConfig = {
      origin: data.origin ?? ENV.TOKENEX.TOKENEX_ORIGIN,
      timestamp: data.timestamp,
      tokenExID: data.tokenExID,
      tokenScheme: data.tokenScheme,
      authenticationKey: data.authenticationKey,
      pci: true,
      cvv: false,
      cvvContainerID: 'CvvTextbox',
      enableAutoComplete: false,
      debug: !isProduction,
      enablePrettyFormat: true,
      styles: {
        base: 'font-size: 16px; line-height: 1.5em; vertical-align: baseline; outline: 0; box-sizing: border-box;-webkit-tap-highlight-color: transparent; color: #484D51; display: block; padding: 12px 15px; width: 100%; height: 41px; border-radius: 8px; border: 1px solid #ECECEC;  background: #FFFFFF;  overflow: hidden;',
        cvv: {
          base: 'font-size: 16px; line-height: 1.5em; vertical-align: baseline; outline: 0; box-sizing: border-box;-webkit-tap-highlight-color: transparent; color: #484D51; display: block; padding: 12px 15px; width: 100%; height: 41px; border-radius: 8px; border: 1px solid #ECECEC;  background: #FFFFFF;  overflow: hidden;',
        },
      },
    };

    //@ts-ignore
    const iframe = new TokenEx.Iframe('tokenExIframeDiv', iframeConfig);

    setTokenexEvents(iframe);

    iframe.load();

    adjustInputElements('field-expiration-date');
    adjustInputElements('field-cvv');
    adjustInputElements('field-first-name');
    adjustInputElements('field-last-name');
    adjustInputElements('field-email');
  };

  useEffect(() => {
    renderIFrame();
  }, []);

  useEffect(() => {
    handleChangeExpirationDate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expiryText]);

  useEffect(() => {
    handleChangeCVV();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [realCVV]);

  useEffect(() => {
    handleChangeFirstName();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstNameText]);

  useEffect(() => {
    handleChangeLastName();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastNameText]);

  useEffect(() => {
    handleChangeEmail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailText]);

  useEffect(() => {
    switch (step) {
      case 1:
        setNextDisabled(!cardNumberValid);
        break;
      case 2:
        setNextDisabled(!firstNameValid && !lastNameValid);
        break;
      case 3:
        setNextDisabled(!emailValid);
        break;
      case 4:
        setNextDisabled(!expiryValid);
        break;
      case 5:
        setNextDisabled(!cvvValid);
        setDisabled(!cvvValid);
        break;
      default:
        setNextDisabled(false);
    }
  }, [step, cardNumberValid, expiryValid, cvvValid, firstNameValid, lastNameValid, emailValid]);

  return (
    <Container className={`step-${step}`}>
      {isLoading && (
        <LoaderContainer>
          <Loader fullScreen={isMobile} />
        </LoaderContainer>
      )}
      <CreditCardImage
        isFlipped={step > 4}
        cardType={cardType === '' ? (typeof flag !== 'undefined' ? flag : '') : cardType}
        cardNumber={cardNumberText}
        holderName={`${firstNameText} ${lastNameText}`}
        expiryDate={expiryText}
        cvv={cvvText}
      />

      <Content>
        {/* Numero */}
        <StepContainer>
          <FieldContainer className={cardNumberError ? 'field-card-number field-error' : 'field-card-number'}>
            <Label htmlFor="card-number">{t('payment.cardForm.cardNumber')}</Label>
            <div id="tokenExIframeDiv"></div>
            {cardNumberError && <Error>{cardNumberError}</Error>}
          </FieldContainer>
        </StepContainer>
        {/* Nombre */}
        <StepContainer>
          <FieldContainer className={firstNameError ? 'field-first-name field-error' : 'field-first-name'}>
            <Label htmlFor="first-name">{t('payment.cardForm.firstName')}</Label>
            <Input
              id="first-name"
              style={{ height: '40px' }}
              onChange={(event) => setFirstNameText(event.target.value)}
            />
            {firstNameError && <Error>{firstNameError}</Error>}
          </FieldContainer>
          <FieldContainer className={lastNameError ? 'field-last-name field-error' : 'field-last-name'}>
            <Label htmlFor="last-name">{t('payment.cardForm.lastName')}</Label>
            <Input
              id="last-name"
              style={{ height: '40px' }}
              onChange={(event) => setLastNameText(event.target.value)}
            />
            {lastNameError && <Error>{lastNameError}</Error>}
          </FieldContainer>
        </StepContainer>
        {/* Email */}
        <StepContainer>
          <FieldContainer className={lastNameError ? 'field-email field-error' : 'field-email'}>
            <Label htmlFor="email">{t('payment.cardForm.email')}</Label>
            <Input
              id="email"
              type="email"
              style={{ height: '40px' }}
              onChange={(event) => setEmailText(event.target.value)}
            />
            {emailError && <Error>{emailError}</Error>}
          </FieldContainer>
        </StepContainer>
        {/* CVV */}
        <StepContainer>
          <FieldContainer className={cvvError ? 'field-cvv field-error' : 'field-cvv'}>
            <Label htmlFor="cvv">{t('payment.cardForm.cvv')}</Label>
            <Input
              mask={cardType === 'amex' || cardType === 'americanExpress' ? '9999' : '999'}
              id="cvv"
              name="cvv"
              onChange={(event) => setRealCVV(event.target.value)}
              style={{ height: '41px' }}
            />
            {cvvError && <Error>{cvvError}</Error>}
          </FieldContainer>
        </StepContainer>
        {/* Fecha */}
        <StepContainer>
          <FieldContainer className={expirationError ? 'field-expiration-date field-error' : 'field-expiration-date'}>
            <Label htmlFor="date">{t('payment.cardForm.expirationDate')}</Label>
            <Input
              mask="99/99"
              id="expiration-date"
              name="expiration-date"
              onChange={(event) => setExpiryText(event.target.value)}
              style={{ height: '41px' }}
            />
            {expirationError && <Error>{expirationError}</Error>}
          </FieldContainer>
        </StepContainer>
        {/* <WarningContainer>
          <img src="/corporate/images/payment/addcard-warning.svg" alt="" />
          <span>{htmlT(t, 'payment.cardForm.warning')}</span>
        </WarningContainer> */}
        <ButtonNavContainer>
          <Button className="btn-prev" onClick={handlePrev}>
            <BsArrowLeft />
          </Button>
          <Button disabled={nextDisabled} className="btn-next" onClick={handleNext}>
            <BsArrowRight />
          </Button>
        </ButtonNavContainer>
        <ButtonContainer>
          <Button
            className="btn-register-cc"
            onClick={handleAddPaymentCard}
            disabled={(disabled && nextDisabled) || isSubmitting}
          >
            {isSubmitting ? <LoaderButton fontColor={ColorPalette.HEX.White} /> : t('payment.cardForm.btnSave')}
          </Button>
        </ButtonContainer>
        <input type="hidden" id="token"></input>
      </Content>
    </Container>
  );
};

export default CreditCard;
